import confetti from 'canvas-confetti'
import { PartyPopper, QrCode, Sparkles, Ticket } from 'lucide-react'
import { useEffect } from 'react'
import { RULES } from '@sdg/shared'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog'

/** The deck's palette, so the confetti belongs to the event. */
const COLOURS = ['#FCA311', '#2E9E5B', '#0F8B8D', '#E4572E', '#14213D', '#5360A8']

/**
 * Two party crackers, fired inward from the lower corners. Respects
 * prefers-reduced-motion: some students will have it on, and a screenful of
 * flying particles is exactly what that setting exists to prevent.
 */
function fireCrackers() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  const shared = {
    particleCount: 60,
    spread: 62,
    startVelocity: 55,
    ticks: 180,
    colors: COLOURS,
    zIndex: 70,
    disableForReducedMotion: true,
  } as const

  confetti({ ...shared, angle: 62, origin: { x: 0.02, y: 0.82 } })
  confetti({ ...shared, angle: 118, origin: { x: 0.98, y: 0.82 } })

  // A softer second burst overhead, so it rains rather than just pops.
  window.setTimeout(() => {
    confetti({
      particleCount: 70,
      spread: 110,
      startVelocity: 32,
      decay: 0.92,
      gravity: 0.9,
      ticks: 220,
      colors: COLOURS,
      origin: { x: 0.5, y: 0.25 },
      zIndex: 70,
      disableForReducedMotion: true,
    })
  }, 260)
}

export function Celebrate({
  open,
  onOpenChange,
  returning,
  name,
  regNo,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** True when this signed an existing student back in. */
  returning: boolean
  name: string
  regNo: string | null
}) {
  useEffect(() => {
    // Only a first registration gets crackers; coming back is not an occasion.
    if (open && !returning) fireCrackers()
  }, [open, returning])

  const firstName = name.trim().split(/\s+/)[0]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showClose={false} className="overflow-hidden p-0">
        <div
          className={
            'flex flex-col items-center px-6 pb-6 pt-7 text-center ' +
            (returning
              ? 'bg-linear-to-b from-teal-soft/70 to-white'
              : 'bg-linear-to-b from-amber-soft to-white')
          }
        >
          <span
            className={
              'mb-3 grid size-14 place-items-center rounded-full ' +
              (returning ? 'bg-teal text-white' : 'bg-amber text-navy-950')
            }
          >
            {returning ? (
              <Ticket className="size-7" strokeWidth={2.2} />
            ) : (
              <PartyPopper className="size-7" strokeWidth={2.2} />
            )}
          </span>

          <DialogTitle>
            {returning ? 'Welcome back' : `You're in, ${firstName}!`}
          </DialogTitle>

          <DialogDescription className="mt-1.5">
            {returning ? (
              <>
                Your account already exists, so no second pass was created — this is the
                pass you already had.
              </>
            ) : (
              <>
                Registration complete. Your digital pass is ready — show its QR code at any
                booth to start collecting stamps.
              </>
            )}
          </DialogDescription>

          {regNo && (
            <div className="mt-4 rounded-xl border border-line bg-white px-5 py-3 shadow-pill">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted">
                Your registration number
              </p>
              <p className="text-3xl font-bold leading-none tracking-wider text-navy-950 tabular-nums">
                {regNo}
              </p>
            </div>
          )}
          {!returning && (
            <ul className="mx-auto mt-4 w-fit space-y-1.5 text-left text-sm">
              <li className="flex items-start justify-center gap-2 text-[12.5px] text-muted">
                <QrCode
                  className="mt-px size-4 shrink-0 text-teal"
                  strokeWidth={2.2}
                />
                <span>Show your QR code at each booth to be marked present.</span>
              </li>

              <li className="flex items-start justify-center gap-2 text-[12.5px] text-muted">
                {/* Second item */}
              </li>
            </ul>
          )}

          <Button block size="lg" className="mt-5" onClick={() => onOpenChange(false)} autoFocus>
            {returning ? 'Open my pass' : 'Show me my pass'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
